import { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from '@/hooks/use-toast';
import {
  X,
  Download,
  Upload,
  FileText,
  Trash2,
  CheckCircle2,
  AlertCircle,
  Loader2,
} from 'lucide-react';
import { useQueryClient } from '@tanstack/react-query';
import { usePreviewProductImport, useCommitProductImport } from '@/hooksApi/useProductCatalog';
import { fetchProductImportTemplate } from '@/api/productCatalogService';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import type {
  ProductCatalogImportRow,
  ProductCatalogImportPreviewResponse,
} from '@/types/productCatalog';

interface CsvImportModalProps {
  open: boolean;
  onClose: () => void;
  onSuccess?: () => void;
}

type Step = 1 | 2 | 3;

const STEP_KEYS: Record<Step, string> = {
  1: 'upload_file',
  2: 'preview_data',
  3: 'import',
};

function formatFileSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

export const CsvImportModal = ({ open, onClose, onSuccess }: CsvImportModalProps) => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();

  const [step, setStep] = useState<Step>(1);
  const [file, setFile] = useState<File | null>(null);
  const [isDragOver, setIsDragOver] = useState(false);
  const [previewData, setPreviewData] = useState<
    ProductCatalogImportPreviewResponse['object'] | null
  >(null);
  const [commitResult, setCommitResult] = useState<{
    imported: number;
    failed: number;
    total: number;
  } | null>(null);
  const [isDownloadingTemplate, setIsDownloadingTemplate] = useState(false);

  const fileInputRef = useRef<HTMLInputElement>(null);

  const { mutate: previewImport, isLoading: isPreviewing } = usePreviewProductImport();
  const { mutate: commitImport, isLoading: isCommitting } = useCommitProductImport();

  if (!open) return null;

  // ─── Handlers ─────────────────────────────────────────────────────────────

  const handleDownloadTemplate = async () => {
    setIsDownloadingTemplate(true);
    try {
      const blob = await fetchProductImportTemplate();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'products-template.csv';
      a.click();
      URL.revokeObjectURL(url);
    } finally {
      setIsDownloadingTemplate(false);
    }
  };

  const applyFile = (incoming: File) => {
    setFile(incoming);
  };

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const selected = e.target.files?.[0];
    if (selected) applyFile(selected);
    // Reset so same file can be re-selected after removal
    e.target.value = '';
  };

  const handleDragOver = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDragOver(true);
  };

  const handleDragLeave = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDragOver(false);
  };

  const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDragOver(false);
    const dropped = e.dataTransfer.files[0];
    if (dropped) applyFile(dropped);
  };

  const handleRemoveFile = () => {
    setFile(null);
  };

  const handleNext = () => {
    if (step === 1 && file) {
      previewImport(file, {
        onSuccess: (data) => {
          setPreviewData(data.object);
          setStep(2);
        },
        onError: () => { toast({ title: String(t('error_title')), description: String(t('operation_failed_please_try_again')), variant: 'destructive' }); },
      });
    }
  };

  const handleImport = () => {
    if (!file) return;
    commitImport(file, {
      onSuccess: (data) => {
        const total = (data.object.imported ?? 0) + (data.object.failed ?? 0);
        setCommitResult({
          imported: data.object.imported,
          failed: data.object.failed,
          total,
        });
        setStep(3);
        queryClient.invalidateQueries(['product-catalog']);
        onSuccess?.();
      },
      onError: () => { toast({ title: String(t('error_title')), description: String(t('operation_failed_please_try_again')), variant: 'destructive' }); },
    });
  };

  const handleDone = () => {
    handleClose();
  };

  const handleClose = () => {
    setStep(1);
    setFile(null);
    setPreviewData(null);
    setCommitResult(null);
    setIsDragOver(false);
    onClose();
  };

  const handleBack = () => {
    if (step === 2) setStep(1);
  };

  // ─── Derived values ────────────────────────────────────────────────────────

  const validRows = previewData?.valid_rows ?? 0;
  const invalidRows = previewData?.invalid_rows ?? 0;
  const rows: ProductCatalogImportRow[] = previewData?.rows ?? [];

  // ─── Render ────────────────────────────────────────────────────────────────

  return (
    <div
      className='fixed inset-0 z-50 flex items-center justify-center bg-black/30 backdrop-blur-[1px]'
      onClick={(e) => {
        if (e.target === e.currentTarget) handleClose();
      }}
    >
      <div className='bg-sd-card rounded-2xl shadow-xl max-w-2xl w-full mx-4 max-h-[90vh] flex flex-col sd-scope sd-font'>
        {/* ── Header ── */}
        <div className='flex items-center justify-between px-6 py-4 border-b border-sd-border shrink-0'>
          <div>
            <h2 className='text-base font-semibold text-sd-foreground'>
              {String(t('import_from_csv', { defaultValue: 'Import from CSV' }))}
            </h2>
          </div>
          <button
            onClick={handleClose}
            className='p-2 rounded-lg hover:bg-sd-muted transition-colors text-sd-muted-foreground hover:text-sd-foreground'
            aria-label={String(t('close', { defaultValue: 'Close' }))}
          >
            <X size={18} />
          </button>
        </div>

        {/* ── Step indicator ── */}
        <div className='px-6 pt-5 pb-2 shrink-0'>
          {/* Circles + connectors */}
          <div className='flex items-center justify-center'>
            {([1, 2, 3] as Step[]).map((s, idx) => (
              <div key={s} className='flex items-center'>
                <div
                  className={`flex items-center justify-center w-8 h-8 rounded-full text-xs font-semibold transition-colors ${
                    step > s
                      ? 'bg-sd-primary text-sd-primary-foreground'
                      : step === s
                        ? 'bg-sd-primary text-sd-primary-foreground ring-4 ring-sd-primary/20'
                        : 'bg-sd-muted text-sd-muted-foreground'
                  }`}
                >
                  {step > s ? <CheckCircle2 size={14} /> : s}
                </div>
                {idx < 2 && (
                  <div
                    className={`w-16 h-0.5 transition-colors ${
                      step > s ? 'bg-sd-primary' : 'bg-sd-border'
                    }`}
                  />
                )}
              </div>
            ))}
          </div>
          {/* Labels */}
          <div className='flex items-start justify-center mt-2'>
            {([1, 2, 3] as Step[]).map((s, idx) => (
              <div key={s} className='flex items-start'>
                <span
                  className={`text-[11px] font-medium text-center whitespace-nowrap w-8 ${
                    step >= s ? 'text-sd-foreground' : 'text-sd-muted-foreground'
                  }`}
                >
                  {String(t(STEP_KEYS[s]))}
                </span>
                {idx < 2 && <div className='w-16' />}
              </div>
            ))}
          </div>
        </div>

        {/* ── Body ── */}
        <div className='flex-1 overflow-y-auto px-6 py-4'>
          {step === 1 && (
            <StepUpload
              file={file}
              isDragOver={isDragOver}
              isDownloadingTemplate={isDownloadingTemplate}
              fileInputRef={fileInputRef}
              onDownloadTemplate={handleDownloadTemplate}
              onDragOver={handleDragOver}
              onDragLeave={handleDragLeave}
              onDrop={handleDrop}
              onFileSelect={handleFileSelect}
              onRemoveFile={handleRemoveFile}
              t={t}
            />
          )}
          {step === 2 && (
            <StepPreview
              rows={rows}
              validRows={validRows}
              invalidRows={invalidRows}
              totalRows={previewData?.total_rows ?? rows.length}
              t={t}
            />
          )}
          {step === 3 && commitResult && (
            <StepResults
              totalRows={commitResult.total}
              imported={commitResult.imported}
              failed={commitResult.failed}
              t={t}
            />
          )}
        </div>

        {/* ── Footer ── */}
        <div className='flex items-center justify-end gap-3 px-6 py-4 border-t border-sd-border shrink-0'>
          {step === 1 && (
            <>
              <button
                onClick={handleClose}
                className='px-4 py-2 rounded-lg border border-sd-input bg-sd-background text-sm font-medium hover:bg-sd-muted transition-colors text-sd-foreground'
              >
                {String(t('cancel', { defaultValue: 'Cancel' }))}
              </button>
              <button
                onClick={handleNext}
                disabled={!file || isPreviewing}
                className='flex items-center gap-2 px-4 py-2 rounded-lg bg-sd-primary text-sd-primary-foreground text-sm font-semibold hover:opacity-90 transition-opacity disabled:opacity-50 disabled:cursor-not-allowed'
              >
                {isPreviewing ? (
                  <Loader2 size={16} className='animate-spin' />
                ) : (
                  <Upload size={16} />
                )}
                {isPreviewing
                  ? String(t('loading', { defaultValue: 'Loading...' }))
                  : String(t('preview_data', { defaultValue: 'Preview Data' }))}
              </button>
            </>
          )}

          {step === 2 && (
            <>
              <button
                onClick={handleBack}
                disabled={isCommitting}
                className='px-4 py-2 rounded-lg border border-sd-input bg-sd-background text-sm font-medium hover:bg-sd-muted transition-colors text-sd-foreground disabled:opacity-50'
              >
                {String(t('back', { defaultValue: 'Back' }))}
              </button>
              <button
                onClick={handleImport}
                disabled={isCommitting || validRows === 0}
                className='flex items-center gap-2 px-4 py-2 rounded-lg bg-sd-primary text-sd-primary-foreground text-sm font-semibold hover:opacity-90 transition-opacity disabled:opacity-50 disabled:cursor-not-allowed'
              >
                {isCommitting ? (
                  <Loader2 size={16} className='animate-spin' />
                ) : (
                  <CheckCircle2 size={16} />
                )}
                {isCommitting
                  ? String(t('importing', { defaultValue: 'Importing...' }))
                  : `${String(t('import', { defaultValue: 'Import' }))} ${validRows} ${String(t('valid_products', { defaultValue: 'valid products' }))}`}
              </button>
            </>
          )}

          {step === 3 && (
            <button
              onClick={handleDone}
              className='px-4 py-2 rounded-lg bg-sd-primary text-sd-primary-foreground text-sm font-semibold hover:opacity-90 transition-opacity'
            >
              {String(t('done', { defaultValue: 'Done' }))}
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

// ─── Step 1: Upload ────────────────────────────────────────────────────────────

interface StepUploadProps {
  file: File | null;
  isDragOver: boolean;
  isDownloadingTemplate: boolean;
  fileInputRef: React.RefObject<HTMLInputElement>;
  onDownloadTemplate: () => void;
  onDragOver: (e: React.DragEvent<HTMLDivElement>) => void;
  onDragLeave: (e: React.DragEvent<HTMLDivElement>) => void;
  onDrop: (e: React.DragEvent<HTMLDivElement>) => void;
  onFileSelect: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onRemoveFile: () => void;
  t: (key: string, options?: Record<string, unknown>) => string | unknown;
}

const StepUpload = ({
  file,
  isDragOver,
  isDownloadingTemplate,
  fileInputRef,
  onDownloadTemplate,
  onDragOver,
  onDragLeave,
  onDrop,
  onFileSelect,
  onRemoveFile,
  t,
}: StepUploadProps) => (
  <div className='space-y-4'>
    {/* Instructions */}
    <div className='rounded-xl border border-sd-border bg-sd-muted/30 p-4 space-y-2'>
      <p className='text-sm font-medium text-sd-foreground'>
        {String(t('csv_import_instructions_title', { defaultValue: 'How to import products' }))}
      </p>
      <ol className='list-decimal list-inside space-y-1 text-sm text-sd-muted-foreground ps-1'>
        <li>{String(t('csv_step_1_download', { defaultValue: 'Download the sample CSV template below' }))}</li>
        <li>{String(t('csv_step_2_fill', { defaultValue: 'Fill in your product data' }))}</li>
        <li>{String(t('csv_step_3_upload', { defaultValue: 'Upload the completed file' }))}</li>
      </ol>
      {/* Column hint */}
      <div className='mt-3 pt-3 border-t border-sd-border'>
        <p className='text-xs text-sd-muted-foreground'>
          {String(t('csv_columns_hint', { defaultValue: 'Required and optional columns:' }))}
        </p>
        <div className='flex flex-wrap gap-1.5 mt-2'>
          {['title', 'price', 'category'].map((col) => (
            <span
              key={col}
              className='inline-flex items-center px-2 py-0.5 rounded-md bg-sd-muted text-xs font-mono text-sd-muted-foreground'
            >
              {col}
            </span>
          ))}
        </div>
      </div>
    </div>

    {/* Download template */}
    <button
      onClick={onDownloadTemplate}
      disabled={isDownloadingTemplate}
      className='flex items-center gap-2 text-sm font-medium text-sd-primary hover:opacity-80 transition-opacity disabled:opacity-50'
    >
      {isDownloadingTemplate ? (
        <Loader2 size={16} className='animate-spin' />
      ) : (
        <Download size={16} />
      )}
      {String(t('download_sample_file', { defaultValue: 'Download sample file' }))}
    </button>

    {/* Drop zone */}
    <div
      onDragOver={onDragOver}
      onDragLeave={onDragLeave}
      onDrop={onDrop}
      className={`border-2 border-dashed rounded-xl p-8 text-center transition-colors cursor-pointer ${
        isDragOver
          ? 'border-sd-primary bg-sd-primary/5'
          : 'border-sd-border bg-sd-muted/20 hover:border-sd-primary/50 hover:bg-sd-muted/30'
      }`}
      onClick={() => fileInputRef.current?.click()}
    >
      <Upload size={32} className='mx-auto mb-3 text-sd-muted-foreground' />
      <p className='text-sm font-medium text-sd-foreground mb-1'>
        {String(t('drag_and_drop_csv', { defaultValue: 'Drag and drop your CSV file here' }))}
      </p>
      <p className='text-xs text-sd-muted-foreground mb-3'>
        {String(t('or', { defaultValue: 'or' }))}
      </p>
      <span className='px-4 py-2 rounded-lg bg-sd-primary text-sd-primary-foreground text-sm font-semibold hover:opacity-90 transition-opacity'>
        {String(t('browse_files', { defaultValue: 'Browse files' }))}
      </span>
      <input
        ref={fileInputRef}
        type='file'
        accept='.csv,.txt'
        className='hidden'
        onChange={onFileSelect}
        onClick={(e) => e.stopPropagation()}
      />
      <p className='text-xs text-sd-muted-foreground mt-3'>
        {String(t('csv_txt_accepted', { defaultValue: '.csv and .txt files accepted' }))}
      </p>
    </div>

    {/* Selected file */}
    {file && (
      <div className='flex items-center justify-between rounded-xl border border-sd-border bg-sd-muted/20 px-4 py-3'>
        <div className='flex items-center gap-3'>
          <div className='flex items-center justify-center w-9 h-9 rounded-lg bg-sd-muted shrink-0'>
            <FileText size={18} className='text-sd-muted-foreground' />
          </div>
          <div className='min-w-0'>
            <p className='text-sm font-medium text-sd-foreground truncate max-w-[300px]'>
              {file.name}
            </p>
            <p className='text-xs text-sd-muted-foreground'>{formatFileSize(file.size)}</p>
          </div>
        </div>
        <button
          onClick={(e) => {
            e.stopPropagation();
            onRemoveFile();
          }}
          className='p-1.5 rounded-lg hover:bg-sd-muted transition-colors text-sd-muted-foreground hover:text-sd-destructive ms-3 shrink-0'
          aria-label={String(t('remove_file', { defaultValue: 'Remove file' }))}
        >
          <Trash2 size={16} />
        </button>
      </div>
    )}
  </div>
);

// ─── Step 2: Preview ───────────────────────────────────────────────────────────

interface StepPreviewProps {
  rows: ProductCatalogImportRow[];
  validRows: number;
  invalidRows: number;
  totalRows: number;
  t: (key: string, options?: Record<string, unknown>) => string | unknown;
}

const StepPreview = ({ rows, validRows, invalidRows, totalRows, t }: StepPreviewProps) => {
  const displayRows = rows.slice(0, 10);

  return (
    <div className='space-y-4'>
      {/* Summary bar */}
      <div className='flex items-center gap-4 flex-wrap rounded-xl border border-sd-border bg-sd-muted/20 px-4 py-3'>
        <span className='text-sm font-medium text-sd-muted-foreground'>
          {String(t('total_rows', { defaultValue: 'Total rows' }))}:{' '}
          <span className='font-semibold text-sd-foreground'>{totalRows}</span>
        </span>
        <span className='flex items-center gap-1 text-sm font-medium text-green-600'>
          <CheckCircle2 size={14} className='text-green-600' />
          {validRows} {String(t('valid', { defaultValue: 'valid' }))}
        </span>
        {invalidRows > 0 && (
          <span className='flex items-center gap-1 text-sm font-medium text-sd-destructive'>
            <AlertCircle size={14} />
            {invalidRows} {String(t('with_errors', { defaultValue: 'with errors' }))}
          </span>
        )}
        {rows.length > 10 && (
          <span className='text-xs text-sd-muted-foreground ms-auto'>
            {String(t('showing_first_10_rows', { defaultValue: 'Showing first 10 rows' }))}
          </span>
        )}
      </div>

      {/* Table */}
      <div className='rounded-xl border border-sd-border overflow-hidden'>
        <div className='overflow-x-auto'>
          <table className='w-full text-sm'>
            <thead>
              <tr className='bg-sd-muted/40 border-b border-sd-border'>
                <th className='px-3 py-2.5 text-start font-medium text-sd-muted-foreground w-10'>
                  #
                </th>
                <th className='px-3 py-2.5 text-start font-medium text-sd-muted-foreground'>
                  {String(t('title', { defaultValue: 'Title' }))}
                </th>
                <th className='px-3 py-2.5 text-start font-medium text-sd-muted-foreground'>
                  {String(t('price', { defaultValue: 'Price' }))}
                </th>
                {/* Quantity hidden — always defaults to 1. Uncomment to re-enable. */}
                <th className='px-3 py-2.5 text-start font-medium text-sd-muted-foreground'>
                  {String(t('category', { defaultValue: 'Category' }))}
                </th>
                <th className='px-3 py-2.5 text-start font-medium text-sd-muted-foreground w-16'>
                  {String(t('status', { defaultValue: 'Status' }))}
                </th>
              </tr>
            </thead>
            <tbody>
              {displayRows.map((row) => {
                const errorMessages = Object.values(row.errors).join(' · ');
                return (
                  <tr
                    key={row.row_number}
                    className={`border-b border-sd-border last:border-0 ${
                      !row.is_valid ? 'bg-sd-destructive/5' : ''
                    }`}
                  >
                    <td className='px-3 py-2.5 text-sd-muted-foreground text-xs'>
                      {row.row_number}
                    </td>
                    <td
                      className={`px-3 py-2.5 ${
                        row.errors['title']
                          ? 'text-sd-destructive font-medium'
                          : 'text-sd-foreground'
                      }`}
                    >
                      {row.data['title'] || '—'}
                    </td>
                    <td
                      className={`px-3 py-2.5 ${
                        row.errors['price']
                          ? 'text-sd-destructive font-medium'
                          : 'text-sd-foreground'
                      }`}
                    >
                      {row.data['price'] || '—'}
                    </td>
                    {/* Quantity hidden — always defaults to 1. Uncomment to re-enable. */}
                    <td className='px-3 py-2.5 text-sd-foreground'>
                      {row.data['category'] || '—'}
                    </td>
                    <td className='px-3 py-2.5'>
                      {row.is_valid ? (
                        <CheckCircle2 size={16} className='text-green-600' />
                      ) : (
                        <TooltipProvider delayDuration={200}>
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <span className='cursor-help inline-flex'>
                                <AlertCircle size={16} className='text-sd-destructive' />
                              </span>
                            </TooltipTrigger>
                            <TooltipContent side='top' className='max-w-xs text-xs'>
                              {errorMessages}
                            </TooltipContent>
                          </Tooltip>
                        </TooltipProvider>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {/* Invalid rows error detail */}
      {invalidRows > 0 && (
        <div className='rounded-xl border border-sd-destructive/30 bg-sd-destructive/5 px-4 py-3 space-y-1'>
          <p className='text-xs font-semibold text-sd-destructive flex items-center gap-1.5'>
            <AlertCircle size={13} />
            {invalidRows} {String(t('rows_have_errors', { defaultValue: 'rows have errors' }))}
          </p>
          <p className='text-xs text-sd-destructive/80'>
            {String(t('fix_errors_before_import', { defaultValue: 'Fix the errors before importing.' }))}
          </p>
        </div>
      )}
    </div>
  );
};

// ─── Step 3: Results ───────────────────────────────────────────────────────────

interface StepResultsProps {
  totalRows: number;
  imported: number;
  failed: number;
  t: (key: string, options?: Record<string, unknown>) => string | unknown;
}

const StepResults = ({ totalRows, imported, failed, t }: StepResultsProps) => (
  <div className='space-y-6'>
    {/* Success icon + title */}
    <div className='flex flex-col items-center py-4'>
      <div className='flex items-center justify-center w-16 h-16 rounded-full bg-green-50/80 mb-3'>
        <CheckCircle2 size={32} className='text-green-600' />
      </div>
      <h3 className='text-base font-semibold text-sd-foreground'>
        {String(t('import_complete', { defaultValue: 'Import complete' }))}
      </h3>
      <p className='text-sm text-sd-muted-foreground mt-1 text-center'>
        {String(t('import_complete_description', { defaultValue: 'Your products have been imported successfully.' }))}
      </p>
    </div>

    {/* Stats grid */}
    <div className='grid grid-cols-3 gap-4'>
      <div className='rounded-xl bg-sd-muted/40 p-4 text-center'>
        <p className='text-2xl font-bold text-sd-foreground'>{totalRows}</p>
        <p className='text-sm text-sd-muted-foreground mt-1'>
          {String(t('total_rows', { defaultValue: 'Total rows' }))}
        </p>
      </div>
      <div className='rounded-xl bg-green-50/80 p-4 text-center'>
        <p className='text-2xl font-bold text-green-700'>{imported}</p>
        <p className='text-sm text-green-600 mt-1'>
          {String(t('imported', { defaultValue: 'Imported' }))}
        </p>
      </div>
      <div className='rounded-xl bg-sd-destructive/5 p-4 text-center'>
        <p className='text-2xl font-bold text-sd-destructive'>{failed}</p>
        <p className='text-sm text-sd-destructive/80 mt-1'>
          {String(t('failed_rows', { defaultValue: 'Failed' }))}
        </p>
      </div>
    </div>
  </div>
);

export default CsvImportModal;
